<script setup lang="ts">
import { computed, defineComponent, h, ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DatePicker from '@/components/DatePicker/DatePicker.vue';
import FilterEnumSelect from '@/components/FilterBuilder/FilterEnumSelect.vue';
import BulkLookupSelect from './BulkLookupSelect.vue';
import ModelSearchPicker from '@/components/ModelSearchPicker.vue';
import TimeOfDayInput from '@/components/schedules/TimeOfDayInput.vue';
import { isSubmitShortcut, submitShortcutLabel } from '@/utils/submitShortcut';
import { useSubmitShortcutFromMenus } from '@/composables/useSubmitShortcutFromMenus';
import { BULK_OP_LABELS, buildBulkChange } from './bulkEditChanges';
import { readRememberedBulkEditRows, rememberBulkEditRows } from './bulkEditMemory';
import { bulkChangeSummary, bulkDialogFields, bulkDisplayValue, bulkValueControl, type BulkDialogField } from './bulkEditFields';
import type { BulkChange, BulkChangeOp, BulkEditableField, BulkOperation } from '@/composables/api/useBulkActionApi';

/**
 * The bulk edit form. The user picks a field, then what to do to it, then a value.
 * They can add more fields, so one edit changes several fields at once.
 *
 * The fields and the actions offered for each come from the capabilities endpoint, and only
 * from there, each named by its label.
 *
 * Custom lists use it. Schedules are edited with the schedule editor's own form instead, one tab
 * per kind of schedule (ScheduleBulkEditDialog).
 *
 * With `embedded`, it draws only the field rows, with no dialog around them. The schedule bulk
 * edit dialog shows them as its "Custom Lists" tab. The parent then reads the edit with the
 * exposed `collect()`, and a row with no field is left out rather than refused.
 *
 * With a `memoryKey`, it opens on the last edit sent to the confirmation, kept in this browser
 * (bulkEditMemory). A field or action the user can no longer pick is dropped from it. The dialog
 * then says the rows come from the last edit, and offers to start empty, so an old value such as
 * a start date is not sent again by accident.
 */
const props = withDefaults(defineProps<{
  fields: BulkEditableField[];
  /** How many rows the edit covers, for the heading. */
  count: number;
  embedded?: boolean;
  /** Where the last edit is remembered. Null remembers nothing, and every opening starts empty. */
  memoryKey?: string | null;
}>(), { embedded: false, memoryKey: null });

/** Stands in for the dialog and its content when embedded. It draws a plain box around the rows. */
const EmbeddedShell = defineComponent({
  name: 'BulkEditEmbeddedShell',
  props: { open: Boolean },
  emits: ['update:open'],
  setup(_props, { slots }) {
    return () => h('div', slots.default?.());
  },
});

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  (e: 'submit', payload: { operation: BulkOperation; valueLabels: Record<string, string>; summary: string[] }): void;
}>();

interface ChangeRow {
  key: number;
  field: string | null;
  op: BulkChangeOp | null;
  /** The control's value as a string. Converted to the contract's JSON type on submit. */
  value: string | null;
  /** The chosen lookup option's name, when the field is a lookup. */
  label: string | null;
}

let nextRowKey = 1;
function emptyRow(): ChangeRow {
  return { key: nextRowKey++, field: null, op: null, value: null, label: null };
}

/** The fields the menu offers, in alphabetical order of their names. */
const dialogFields = computed(() => bulkDialogFields(props.fields));
const fieldsByName = computed(() => new Map(dialogFields.value.map((field) => [field.field, field])));

/**
 * The rows an opening starts with: the last edit sent to the confirmation, or one empty row.
 * A cancelled edit was never remembered, so it does not come back.
 */
function startingRows(): ChangeRow[] {
  if (props.memoryKey === null) return [emptyRow()];
  const remembered = readRememberedBulkEditRows(props.memoryKey)
    .filter((row) => {
      const field = fieldsByName.value.get(row.field);
      return field !== undefined && (row.op === null || field.operations.includes(row.op as BulkChangeOp));
    })
    .filter((row, index, all) => all.findIndex((other) => other.field === row.field) === index)
    .map((row) => ({ ...row, key: nextRowKey++, op: row.op as BulkChangeOp | null }));
  return remembered.length > 0 ? remembered : [emptyRow()];
}

const rows = ref<ChangeRow[]>(startingRows());
const errors = ref<string[]>([]);
/** True while the rows are the remembered edit, untouched. The dialog says so. */
const fromLastEdit = ref(rows.value.some((row) => row.field !== null));

function startOver(fromMemory: boolean) {
  rows.value = fromMemory ? startingRows() : [emptyRow()];
  fromLastEdit.value = fromMemory && rows.value.some((row) => row.field !== null);
  errors.value = [];
}

watch(open, (isOpen) => {
  if (isOpen) startOver(true);
});

// The fields can arrive after the dialog opened, and every remembered row was dropped for want
// of them. Once they come, an opening the user has not touched yet starts from the memory.
watch(dialogFields, () => {
  if (rows.value.length === 1 && rows.value[0]!.field === null) startOver(true);
});

// Any change by the user makes the rows their own edit, so the note goes. The label is left out:
// a lookup reports its option's name by itself once the options load.
watch(
  () => JSON.stringify(rows.value.map((row) => [row.key, row.field, row.op, row.value])),
  () => {
    fromLastEdit.value = false;
  },
  { flush: 'sync' },
);
const capabilityFieldsByName = computed(() => new Map(props.fields.map((field) => [field.field, field])));

function fieldFor(row: ChangeRow): BulkDialogField | null {
  return row.field === null ? null : (fieldsByName.value.get(row.field) ?? null);
}

/** A field may appear only once in an edit, so each row offers the fields no other row has taken. */
function fieldOptionsFor(row: ChangeRow): BulkDialogField[] {
  const taken = new Set(rows.value.filter((other) => other.key !== row.key).map((other) => other.field));
  return dialogFields.value.filter((field) => !taken.has(field.field));
}

const canAddRow = computed(() => rows.value.length < dialogFields.value.length);

function addRow() {
  if (canAddRow.value) rows.value = [...rows.value, emptyRow()];
}

function removeRow(key: number) {
  rows.value = rows.value.length === 1 ? [emptyRow()] : rows.value.filter((row) => row.key !== key);
}

function setField(row: ChangeRow, fieldName: string | null) {
  row.field = fieldName;
  const field = fieldFor(row);
  // "Set to" comes first whenever a field offers it. It is the common case.
  row.op = field ? (field.operations.includes('set') ? 'set' : (field.operations[0] ?? null)) : null;
  row.value = null;
  row.label = null;
}

function setOp(row: ChangeRow, op: string | null) {
  row.op = (op as BulkChangeOp | null) ?? null;
  row.value = null;
  row.label = null;
}

/** Which input the value needs. A shift is always a number of days, whatever the field's type. */
function controlFor(row: ChangeRow): string | null {
  const field = fieldFor(row);
  if (!field || row.op === null || row.op === 'clear') return null;
  if (row.op === 'shift_days') return 'shift_days';
  return bulkValueControl(field);
}


interface BooleanOption {
  value: string;
  label: string;
}

const BOOLEAN_OPTIONS: BooleanOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

interface CollectedEdit {
  changes: BulkChange[];
  valueLabels: Record<string, string>;
  summary: string[];
}

/**
 * The edit the rows describe, or null when a row has a problem, which is then shown. Embedded, a
 * row with no field is left out, so an untouched tab gives an edit with no changes.
 */
function collect(): CollectedEdit | null {
  const changes: BulkChange[] = [];
  const problems: string[] = [];
  const valueLabels: Record<string, string> = {};

  for (const row of rows.value) {
    const field = fieldFor(row);
    if (!field && props.embedded) continue;
    if (!field) {
      problems.push('Choose a field for every row, or remove the empty row.');
      continue;
    }
    const capabilityField = capabilityFieldsByName.value.get(field.field)!;
    const built = buildBulkChange(capabilityField, row.op, row.value);
    if (built.error !== undefined) {
      problems.push(built.error);
      continue;
    }
    changes.push(built.change);
    if (field.type === 'lookup' && row.label) valueLabels[field.field] = row.label;
    const shown = built.change.op === 'set' && row.value !== null ? bulkDisplayValue(field, row.value) : undefined;
    if (shown !== undefined) valueLabels[field.field] = shown;
  }

  errors.value = problems;
  if (problems.length > 0) return null;
  return { changes, valueLabels, summary: bulkChangeSummary(changes, props.fields, valueLabels) };
}

/** True once any row has a field, for the tab's "changed" dot. */
const hasChanges = computed(() => rows.value.some((row) => row.field !== null));

/** Keep the rows as the next opening's start. Called once the edit goes to the confirmation. */
function remember(): void {
  if (props.memoryKey === null) return;
  rememberBulkEditRows(
    props.memoryKey,
    rows.value
      .filter((row): row is ChangeRow & { field: string } => row.field !== null)
      .map((row) => ({ field: row.field, op: row.op, value: row.value, label: row.label })),
  );
}

defineExpose({ collect, hasChanges, remember });

function submit() {
  const edit = collect();
  if (!edit || edit.changes.length === 0) return;
  remember();
  emit('submit', { operation: { action: 'update', changes: edit.changes }, valueLabels: edit.valueLabels, summary: edit.summary });
}

/**
 * Cmd+Enter checks the changes, as the button does. Embedded, the dialog does not listen at all,
 * and the key is left for the parent dialog, which checks every tab at once.
 */
function onKeydown(event: KeyboardEvent) {
  if (!isSubmitShortcut(event)) return;
  event.preventDefault();
  submit();
}

useSubmitShortcutFromMenus(() => open.value && !props.embedded, onKeydown);

const submitShortcut = submitShortcutLabel();

function fieldLabel(field: BulkDialogField): string {
  return field.label;
}
</script>

<template>
  <!-- Embedded, the shell stands in for both the dialog and its content, so the rows are drawn once. -->
  <component :is="embedded ? EmbeddedShell : Dialog" v-model:open="open">
    <!-- Sized like the inspector's center peek: most of the screen, and the whole of a phone's.
         The rows scroll inside it, and the buttons stay at the bottom. -->
    <component
      :is="embedded ? EmbeddedShell : DialogContent"
      :class="embedded
        ? 'flex min-h-0 flex-col gap-4'
        : 'flex min-h-0 max-w-none flex-col gap-4 overflow-hidden sm:max-w-none h-[100dvh] w-[100vw] rounded-none max-sm:top-0 max-sm:translate-y-0 sm:h-[75vh] sm:w-[75vw] sm:rounded-lg'"
      :data-testid="embedded ? 'bulk-edit-fields' : 'bulk-edit-dialog'"
      v-on="embedded ? {} : { keydown: onKeydown }"
    >
      <DialogHeader v-if="!embedded">
        <DialogTitle>Bulk edit {{ count.toLocaleString() }} row{{ count === 1 ? '' : 's' }}</DialogTitle>
        <DialogDescription>
          Pick a field, what to do to it, and the value. Nothing is saved until you confirm on the next step.
        </DialogDescription>
      </DialogHeader>

      <div class="-mx-1 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-1">
        <div
          v-if="fromLastEdit"
          class="text-muted-foreground flex flex-wrap items-center gap-2 text-sm"
          role="status"
          data-testid="bulk-edit-from-last-edit"
        >
          <span>These are the fields and values from your last bulk edit. Check each one before you continue.</span>
          <Button variant="outline" size="sm" data-testid="bulk-edit-start-empty" @click="startOver(false)">Start empty</Button>
        </div>
        <div
          v-for="(row, index) in rows"
          :key="row.key"
          class="grid grid-cols-1 items-start gap-2 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,1.4fr)_auto]"
          :data-testid="`bulk-edit-row-${index}`"
        >
          <FilterEnumSelect
            :model-value="row.field"
            :options="fieldOptionsFor(row)"
            :get-value="(field: BulkDialogField) => field.field"
            :get-label="(field: BulkDialogField) => fieldLabel(field)"
            placeholder="Field…"
            :test-id-prefix="`bulk-edit-field-${index}`"
            @update:model-value="(value: string | null | undefined) => setField(row, value ?? null)"
          />
          <FilterEnumSelect
            v-if="fieldFor(row)"
            :model-value="row.op"
            :options="fieldFor(row)!.operations"
            :get-value="(op: BulkChangeOp) => op"
            :get-label="(op: BulkChangeOp) => BULK_OP_LABELS[op]"
            placeholder="Action…"
            :test-id-prefix="`bulk-edit-op-${index}`"
            @update:model-value="(value: string | null | undefined) => setOp(row, value ?? null)"
          />
          <div v-else class="hidden sm:block" />

          <div class="min-w-0" :data-testid="`bulk-edit-value-${index}`">
            <DatePicker
              v-if="controlFor(row) === 'date'"
              v-model="row.value"
              :label="fieldLabel(fieldFor(row)!)"
            />
            <Input
              v-else-if="controlFor(row) === 'datetime'"
              :model-value="row.value ?? ''"
              @update:model-value="(value: string | number) => (row.value = String(value))"
              type="datetime-local"
              step="1"
              class="h-8 text-sm"
              :data-testid="`bulk-edit-datetime-${index}`"
            />
            <TimeOfDayInput
              v-else-if="controlFor(row) === 'time'"
              v-model="row.value"
              :label="fieldLabel(fieldFor(row)!)"
              :data-testid="`bulk-edit-time-${index}`"
            />
            <div v-else-if="controlFor(row) === 'shift_days'" class="flex items-center gap-2">
              <Input
                :model-value="row.value ?? ''"
                @update:model-value="(value: string | number) => (row.value = String(value))"
                type="number"
                step="1"
                min="-366"
                max="366"
                placeholder="7"
                class="h-8 w-24 text-sm"
                :data-testid="`bulk-edit-shift-${index}`"
              />
              <span class="text-muted-foreground text-xs">days. Use a minus sign to move back.</span>
            </div>
            <FilterEnumSelect
              v-else-if="controlFor(row) === 'boolean'"
              v-model="row.value"
              :options="BOOLEAN_OPTIONS"
              :get-value="(option: BooleanOption) => option.value"
              :get-label="(option: BooleanOption) => option.label"
              placeholder="Yes or no…"
              :test-id-prefix="`bulk-edit-boolean-${index}`"
            />
            <Input
              v-else-if="controlFor(row) === 'integer'"
              :model-value="row.value ?? ''"
              @update:model-value="(value: string | number) => (row.value = String(value))"
              type="number"
              step="1"
              class="h-8 text-sm"
              :data-testid="`bulk-edit-integer-${index}`"
            />
            <BulkLookupSelect
              v-else-if="controlFor(row) === 'lookup'"
              v-model="row.value"
              :slug="fieldFor(row)!.lookup!.slug"
              :test-id-prefix="`bulk-edit-lookup-${index}`"
              @label="(label: string | null) => (row.label = label)"
            />
            <ModelSearchPicker
              v-else-if="controlFor(row) === 'lookup_search'"
              v-model="row.value"
              :slug="fieldFor(row)!.lookup!.slug"
              :test-id-prefix="`bulk-edit-lookup-search-${index}`"
              @label="(label: string | null) => (row.label = label)"
              :initial-label="row.label"
            />
            <Input
              v-else-if="controlFor(row) === 'string'"
              :model-value="row.value ?? ''"
              @update:model-value="(value: string | number) => (row.value = String(value))"
              :maxlength="fieldFor(row)!.max_length ?? undefined"
              class="h-8 text-sm"
              :data-testid="`bulk-edit-string-${index}`"
            />
            <p v-else-if="fieldFor(row) && row.op === 'clear'" class="text-muted-foreground pt-1.5 text-xs">
              The field will be emptied.
            </p>
          </div>

          <button
            type="button"
            class="text-muted-foreground hover:text-foreground hover:bg-muted/40 border-border/60 inline-flex size-8 shrink-0 items-center justify-center rounded-md border"
            :aria-label="`Remove row ${index + 1}`"
            :data-testid="`bulk-edit-remove-${index}`"
            @click="removeRow(row.key)"
          >
            <XMarkIcon class="size-3.5" aria-hidden="true" />
          </button>
        </div>

        <div>
          <Button
            variant="outline"
            size="sm"
            :disabled="!canAddRow"
            data-testid="bulk-edit-add-row"
            @click="addRow"
          >
            Add another field
          </Button>
        </div>

        <ul v-if="errors.length > 0" class="text-destructive list-disc pl-5 text-sm" data-testid="bulk-edit-errors">
          <li v-for="message in errors" :key="message">{{ message }}</li>
        </ul>
      </div>

      <DialogFooter v-if="!embedded">
        <Button variant="outline" data-testid="bulk-edit-cancel" @click="open = false">Cancel</Button>
        <Button data-testid="bulk-edit-submit" :title="`Check the changes (${submitShortcut})`" @click="submit">Check the changes</Button>
      </DialogFooter>
    </component>
  </component>
</template>
